import React from 'react';
import { 
  Shield, MessageSquare, FileText, CreditCard, Star, BarChart, 
  Activity, ArrowRight, Network, PhoneCall, Mail 
} from 'lucide-react';
import './Home.css';

const Home = () => {
  const modules = [
    {
      id: 1,
      name: "Kryptic Vault",
      desc: "Eliminate unsafe sharing with our KMS-backed credential proxy and role-based access.",
      icon: <Shield size={32} color="white" />,
      bgClass: "bg-gradient-purple"
    },
    {
      id: 2,
      name: "Patient Comms",
      desc: "Centralized omnichannel system with SMS, MMS, and AI-powered outbound calls.",
      icon: <MessageSquare size={32} color="white" />,
      bgClass: "bg-gradient-teal"
    },
    {
      id: 3,
      name: "Digital Intake",
      desc: "Digitize consent workflows with version-controlled schemas and structured data.",
      icon: <FileText size={32} color="white" />,
      bgClass: "bg-gradient-dark"
    },
    {
      id: 4,
      name: "Patient AR",
      desc: "Track ledger entries and automate payment reminders with an immutable history.",
      icon: <CreditCard size={32} color="white" />,
      bgClass: "bg-gradient-purple"
    },
    {
      id: 5,
      name: "Patient Reviews",
      desc: "Automate internal feedback capture and trigger conditional external review requests.",
      icon: <Star size={32} color="white" />,
      bgClass: "bg-gradient-teal"
    },
    {
      id: 6,
      name: "Practice Analytics",
      desc: "Cross-module reporting layer with time-series reporting and role-based dashboards.",
      icon: <BarChart size={32} color="white" />,
      bgClass: "bg-gradient-dark"
    },
    {
      id: 7,
      name: "Insurance Verification",
      desc: "Automate real-time and batch eligibility checks with full audit trails.",
      icon: <Activity size={32} color="white" />,
      bgClass: "bg-gradient-purple"
    }
  ];

  return (
    <div className="home-container">
      {/* --- FLOATING COMMUNICATION BACKGROUND --- */}
      <div className="animated-comm-bg">
        {/* Glowing Background Orbs */}
        <div className="glow-orb teal-orb"></div>
        <div className="glow-orb purple-orb"></div>

        {/* Floating Glassmorphism Communication Icons */}
        <div className="float-icon fi-1"><MessageSquare size={35} color="#2CB1BC" /></div>
        <div className="float-icon fi-2"><PhoneCall size={30} color="#9370DB" /></div>
        <div className="float-icon fi-3"><Mail size={40} color="#2CB1BC" /></div>
        <div className="float-icon fi-4"><MessageSquare size={25} color="#9370DB" /></div>
        <div className="float-icon fi-5"><PhoneCall size={35} color="#2CB1BC" /></div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content-wrapper">
          {/* AOS ANIMATION ADDED HERE */}
          <div className="hero-text" data-aos="fade-right">
            <h1>Acurio OS:<br />The Secure Dental<br />Operating System</h1>
            <p>A modular, cloud-based platform designed to centralize core operational workflows while enforcing strict security, role-based access, and auditability.</p>
            <button className="get-started-btn">Request a Demo →</button>
          </div>

          {/* AOS ANIMATION ADDED HERE */}
          <div className="hero-graphic" data-aos="zoom-in" data-aos-delay="200">
            <div className="orbit-center">▲</div>
            <div className="orbit-ring ring-1"></div>
            <div className="orbit-ring ring-2"></div>
            <div className="orbit-ring ring-3"></div>
            
            <div className="floating-pill pill-1">Kryptic<br/>Vault</div>
            <div className="floating-pill pill-2">AI Comms</div>
            <div className="floating-pill pill-3">Patient<br/>AR</div>
            <div className="floating-pill pill-4">Insurance<br/>Verification</div>
            <div className="floating-pill pill-center">Acurio OS</div>
          </div>
        </div>
      </section>

      {/* Rippling-Style Module Grid */}
      <section className="rippling-section">
        {/* AOS ANIMATION ADDED HERE */}
        <div className="rippling-header" data-aos="fade-up">
          <h2>Start with the modules you need, or run them all together</h2>
          <p>Acurio modules work together to help you break down operational silos in your dental practice and ensure HIPAA-aligned security.</p>
        </div>

        <div className="rippling-grid">
          {modules.map((mod, index) => (
            <div key={mod.id} className="rip-card" data-aos="fade-up" data-aos-delay={index * 100}>
              <div className={`rip-icon-box ${mod.bgClass}`}>
                {mod.icon}
              </div>
              <div className="rip-content">
                <div className="rip-title-row">
                  <h3>{mod.name}</h3>
                  <ArrowRight size={20} color="#9ca3af" />
                </div>
                <p>{mod.desc}</p>
                <a href="#demo" className="rip-demo-link">Get a demo</a>
              </div>
            </div>
          ))}
        </div>

        {/* AOS ANIMATION ADDED HERE */}
        <div className="acurio-banner" data-aos="zoom-in-up" data-aos-delay="200">
          <div className="ab-graphic">
            <Network size={80} color="rgba(255,255,255,0.8)" strokeWidth={1} />
          </div>
          <div className="ab-content">
            <h3>Manage your entire dental organization</h3>
            <p>Centralize core operational workflows and manage permissions across all your practice locations in one secure platform, without the manual work.</p>
            <div className="ab-actions">
              <button className="ab-btn-primary">Get a demo</button>
              <button className="ab-btn-secondary">Learn more</button>
            </div>
          </div>
        </div>
      </section>

      {/* "Take the Next Step" Section */}
      <section className="next-step-section">
        {/* AOS ANIMATION ADDED HERE */}
        <div className="next-step-container" data-aos="fade-up">
          <div className="next-step-text">
            <h2>Take the Next Step</h2>
            <div className="brand-underline"></div>
            <p>
              Acurio's Modular Dental OS is more than just a solution—we are a partner in your success. 
              We work alongside you, just as we do with our existing dental practitioners, ensuring a seamless 
              transition with easy migration, customized solutions tailored to your needs, and 24/7 dedicated support. 
            </p>
            <div className="next-step-buttons">
              <button className="btn-ns-pricing">View Pricing</button>
              <button className="btn-ns-demo">Get a Personalized Demo</button>
            </div>
          </div>
          <div className="next-step-image">
            <img src="/homepage.jpg" alt="Dentist treating a patient" />
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;